import { apiFetch } from '@/api/apiFetch';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, KeyRound, Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import AuthShell, { labelStyle, inputStyle, iconStyle } from '@/components/auth/AuthShell';

function submitBtnStyle(loading) {
  return {
    width: '100%', height: 46, borderRadius: 12, border: 'none', color: 'hsl(var(--primary-foreground))',
    fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    opacity: loading ? 0.7 : 1,
  };
}

function ErrorBox({ error }) {
  return (
    <div style={{ background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 10, padding: '12px 16px', color: 'hsl(var(--destructive))', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
      <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
      {error}
    </div>
  );
}

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleRequest(e) {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setNotice(t('auth.recover.sent_notice'));
      setStep('confirm');
    } catch (err) {
      setError(err.message || t('auth.recover.generic_error'));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm(e) {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t('auth.recover.passwords_mismatch'));
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/api/auth/confirm-forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email, code, password }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || t('auth.recover.generic_error'));
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <CheckCircle2 style={{ width: 48, height: 48, color: 'hsl(var(--success))', margin: '8px auto 16px' }} strokeWidth={1.5} />
          <p style={{ fontSize: 15, color: 'hsl(var(--foreground))', margin: '0 0 24px' }}>
            {t('auth.recover.success')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="vx-btn-primary"
            style={submitBtnStyle(false)}
          >
            {t('auth.recover.back_to_login')}
            <ArrowRight style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h2 className="sf-display" style={{ fontSize: 23, fontWeight: 600, color: 'hsl(var(--foreground))', margin: '0 0 4px', letterSpacing: '-0.02em', textAlign: 'center' }}>
        {t('auth.recover.title')}
      </h2>
      <p style={{ fontSize: 14, color: 'hsl(var(--muted-foreground))', margin: '0 0 28px', textAlign: 'center' }}>
        {step === 'request' ? t('auth.recover.subtitle_request') : t('auth.recover.subtitle_confirm')}
      </p>

      {step === 'request' ? (
        <form onSubmit={handleRequest} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label htmlFor="recover-email" style={labelStyle}>{t('auth.recover.email_label')}</label>
            <div style={{ position: 'relative' }}>
              <Mail style={iconStyle} />
              <input
                id="recover-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.placeholder_email')}
                autoComplete="username"
                inputMode="email"
                spellCheck={false}
                required
                className="vx-input"
                style={inputStyle}
              />
            </div>
          </div>

          {error && <ErrorBox error={error} />}

          <button type="submit" disabled={loading} className="vx-btn-primary" style={submitBtnStyle(loading)}>
            {loading ? (
              <><Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} /> {t('auth.recover.sending')}</>
            ) : (
              <>{t('auth.recover.send_code')} <ArrowRight style={{ width: 16, height: 16 }} /></>
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleConfirm} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {notice && (
            <div style={{ background: 'hsl(var(--primary) / 0.1)', border: '1px solid hsl(var(--primary) / 0.25)', borderRadius: 10, padding: '12px 16px', color: 'hsl(var(--primary))', fontSize: 13 }}>
              {notice}
            </div>
          )}

          <div>
            <label htmlFor="recover-code" style={labelStyle}>{t('auth.recover.code_label')}</label>
            <div style={{ position: 'relative' }}>
              <KeyRound style={iconStyle} />
              <input
                id="recover-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('auth.recover.code_placeholder')}
                autoComplete="one-time-code"
                inputMode="numeric"
                required
                className="vx-input"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label htmlFor="recover-password" style={labelStyle}>{t('auth.recover.new_password_label')}</label>
            <div style={{ position: 'relative' }}>
              <Lock style={iconStyle} />
              <input
                id="recover-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                className="vx-input"
                style={{ ...inputStyle, paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'hsl(var(--muted-foreground))' }}
              >
                {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="recover-confirm-password" style={labelStyle}>{t('auth.recover.confirm_password_label')}</label>
            <div style={{ position: 'relative' }}>
              <Lock style={iconStyle} />
              <input
                id="recover-confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                className="vx-input"
                style={inputStyle}
              />
            </div>
            <p style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', margin: '6px 0 0' }}>
              {t('auth.recover.password_requirements')}
            </p>
          </div>

          {error && <ErrorBox error={error} />}

          <button type="submit" disabled={loading} className="vx-btn-primary" style={submitBtnStyle(loading)}>
            {loading ? (
              <><Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} /> {t('auth.recover.submitting')}</>
            ) : (
              <>{t('auth.recover.submit')} <ArrowRight style={{ width: 16, height: 16 }} /></>
            )}
          </button>

          <button
            type="button"
            onClick={handleRequest}
            disabled={loading}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'hsl(var(--primary))', fontWeight: 500 }}
          >
            {t('auth.recover.resend')}
          </button>
        </form>
      )}

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Link
          to="/login"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'hsl(var(--muted-foreground))', textDecoration: 'none' }}
        >
          <ArrowLeft style={{ width: 14, height: 14 }} />
          {t('auth.recover.back_to_login')}
        </Link>
      </div>
    </AuthShell>
  );
}
